import { create } from 'zustand';
import { userApi } from '../lib/api';

interface User {
  id: number;
  telegram_id: string;
  username: string | null;
  first_name: string;
  last_name: string | null;
  wallet_address: string | null;
  balance_usd: number;
  total_earned_usd: number;
  tasks_completed: number;
  reputation_score: number;
  is_admin: boolean;
}

interface UserStats {
  balance_usd: number;
  total_earned_usd: number;
  tasks_completed: number;
  pending_submissions: number;
  reputation_score: number;
  rank: number;
}

interface UserStore {
  user: User | null;
  stats: UserStats | null;
  isLoading: boolean;
  error: string | null;

  initUser: (data: {
    telegram_id: string;
    first_name: string;
    last_name?: string;
    username?: string;
  }) => Promise<void>;

  refreshStats: () => Promise<void>;
  updateWallet: (address: string) => Promise<void>;
  clearError: () => void;
  setNotLoading: () => void;
}

export const useUserStore = create<UserStore>((set, get) => ({
  user: null,
  stats: null,
  isLoading: true,
  error: null,

  initUser: async (data) => {
    try {
      set({ isLoading: true, error: null });

      const user = await userApi.auth(data) as User;
      const stats = await userApi.getStats(data.telegram_id) as UserStats;

      set({ user, stats, isLoading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to initialize',
        isLoading: false,
      });
    }
  },

  refreshStats: async () => {
    const { user } = get();
    if (!user) return;

    try {
      const stats = await userApi.getStats(user.telegram_id) as UserStats;
      set({ stats });
    } catch (error) {
      console.error('Failed to refresh stats:', error);
    }
  },

  updateWallet: async (address: string) => {
    const { user } = get();
    if (!user) return;

    try {
      await userApi.updateWallet(user.telegram_id, address);
      set({ user: { ...user, wallet_address: address } });
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to update wallet' });
      throw error;
    }
  },

  clearError: () => set({ error: null }),
  setNotLoading: () => set({ isLoading: false }),
}));
